import { z } from 'zod';
import { canonicalJson } from './canonical';
import type { TaskType } from './planning';
import { formatMoney } from './offers';
import { chargeLineGr, chargeLineText, type ProtocolCharges } from './service-charges';
import { sha256Hex } from './sha256';

/**
 * Protokoły (P6, docs/05 rozdz. 10): szablony per branża i rodzaj, kanoniczna treść podpisywana na telefonie
 * (offline), skrót SHA-256 liczony tym samym kodem na telefonie i sprawdzany na serwerze, wspólny układ
 * treści dla podsumowania na telefonie i PDF na serwerze.
 */

export const PROTOCOL_KINDS = {
  montaz: 'Protokół odbioru montażu',
  uruchomienie: 'Protokół uruchomienia',
  przeglad: 'Protokół przeglądu',
  serwis: 'Protokół serwisowy',
} as const;
export type ProtocolKind = keyof typeof PROTOCOL_KINDS;

/** Rodzaj protokołu wymaganego do zakończenia zadania danego typu. */
export const TASK_PROTOCOL_KIND: Partial<Record<TaskType, ProtocolKind>> = { montaz: 'montaz', uruchomienie: 'uruchomienie', przeglad: 'przeglad', serwis: 'serwis' };

export type ProtocolFieldType = 'liczba' | 'tekst' | 'tak_nie' | 'wybor';

export interface ProtocolFieldDef {
  key: string;
  label: string;
  type: ProtocolFieldType;
  unit?: string;
  min?: number;
  max?: number;
  required: boolean;
  options?: string[];
  /** QA qa10302046 P10: podpowiedź wartości z listy czynności zadania — etykieta czynności (wynik pomiaru) */
  fromChecklist?: string;
}

export interface ProtocolTemplateDef {
  code: string;
  version: number;
  kind: ProtocolKind;
  /** kod branży albo „*” (szablon ogólny) */
  tradeCode: string;
  title: string;
  sections: { title: string; fields: ProtocolFieldDef[] }[];
  /** lista urządzeń z numerami seryjnymi */
  devices: boolean;
  /** oświadczenia drukowane nad podpisem */
  declarations: string[];
}

const f = (key: string, label: string, type: ProtocolFieldType, o: Partial<ProtocolFieldDef> = {}): ProtocolFieldDef => ({ key, label, type, required: true, ...o });

const ODBIOR_DECL = [
  'Zamawiający potwierdza wykonanie prac objętych zakresem i przekazanie instalacji do użytkowania.',
  'Zamawiający otrzymał instrukcję obsługi i został przeszkolony z obsługi urządzeń.',
];

/** Szablony pokazowe (wzory demonstracyjne — do zastąpienia wzorami firmy, pytanie P-05). */
export const DEFAULT_PROTOCOL_TEMPLATES: ProtocolTemplateDef[] = [
  {
    code: 'montaz-PC',
    version: 1,
    kind: 'montaz',
    tradeCode: 'PC',
    title: 'Protokół odbioru montażu pompy ciepła',
    devices: true,
    sections: [
      {
        title: 'Montaż',
        fields: [
          f('posadowienie', 'Posadowienie jednostki zewnętrznej', 'wybor', { options: ['fundament', 'konsola ścienna', 'stopy / podstawa'] }),
          f('dlugosc_linii', 'Długość połączeń hydraulicznych', 'liczba', { unit: 'm', min: 0, max: 200 }),
          f('proba_szczelnosci', 'Próba szczelności wykonana', 'tak_nie'),
          f('cisnienie_proby', 'Ciśnienie próby', 'liczba', { unit: 'bar', min: 0, max: 20 }),
          f('plukanie', 'Płukanie instalacji', 'tak_nie', { required: false }),
        ],
      },
    ],
    declarations: ODBIOR_DECL,
  },
  {
    code: 'montaz-KL',
    version: 1,
    kind: 'montaz',
    tradeCode: 'KL',
    title: 'Protokół odbioru montażu klimatyzacji',
    devices: true,
    sections: [
      {
        title: 'Instalacja chłodnicza',
        fields: [
          f('dlugosc_instalacji', 'Długość instalacji chłodniczej', 'liczba', { unit: 'm', min: 0, max: 100 }),
          f('proznia', 'Próżnia końcowa', 'liczba', { unit: 'Pa', min: 0, max: 5000 }),
          f('czas_prozni', 'Czas utrzymania próżni', 'liczba', { unit: 'min', min: 0, max: 600 }),
          f('dobity_czynnik', 'Dobity czynnik', 'liczba', { unit: 'g', min: 0, max: 5000, required: false }),
          f('skropliny', 'Odprowadzenie skroplin', 'wybor', { options: ['grawitacyjne', 'pompka skroplin'] }),
        ],
      },
    ],
    declarations: ODBIOR_DECL,
  },
  {
    code: 'montaz-PV',
    version: 1,
    kind: 'montaz',
    tradeCode: 'PV',
    title: 'Protokół odbioru instalacji fotowoltaicznej',
    devices: true,
    sections: [
      {
        title: 'Instalacja',
        fields: [
          f('liczba_modulow', 'Liczba modułów', 'liczba', { unit: 'szt.', min: 1, max: 1000 }),
          f('moc', 'Moc instalacji', 'liczba', { unit: 'kWp', min: 0, max: 1000 }),
          f('izolacja', 'Rezystancja izolacji DC', 'liczba', { unit: 'MΩ', min: 0, max: 10000 }),
          f('napiecie', 'Napięcie obwodu otwartego stringu', 'liczba', { unit: 'V', min: 0, max: 1500 }),
          f('zabezpieczenia', 'Zabezpieczenia AC i DC sprawdzone', 'tak_nie'),
          f('osd', 'Zgłoszenie do operatora sieci przekazane', 'tak_nie', { required: false }),
        ],
      },
    ],
    declarations: ODBIOR_DECL,
  },
  {
    // QA qa10302046 P10: pomiary elektryczne (PE) — wyniki w protokole, podpowiadane z listy czynności
    code: 'montaz-PE',
    version: 1,
    kind: 'montaz',
    tradeCode: 'PE',
    title: 'Protokół pomiarów elektrycznych',
    devices: true,
    sections: [
      { title: 'Prace', fields: [f('opis_prac', 'Opis wykonanych prac', 'tekst')] },
      {
        title: 'Pomiary',
        fields: [
          f('izolacja', 'Rezystancja izolacji', 'liczba', { unit: 'MΩ', min: 0, max: 10000, fromChecklist: 'Pomiar rezystancji izolacji' }),
          f('petla_zwarcia', 'Impedancja pętli zwarcia', 'liczba', { unit: 'Ω', min: 0, max: 100, fromChecklist: 'Pomiar impedancji pętli zwarcia' }),
          f('rcd_czas', 'Wyłącznik różnicowoprądowy (RCD) — czas zadziałania', 'liczba', { unit: 'ms', min: 0, max: 1000 }),
          f('rcd_prad', 'Wyłącznik różnicowoprądowy (RCD) — prąd zadziałania', 'liczba', { unit: 'mA', min: 0, max: 1000 }),
          f('ciaglosc', 'Ciągłość przewodów ochronnych', 'tak_nie', { required: false, fromChecklist: 'Ciągłość przewodów ochronnych' }),
          f('wynik', 'Wynik pomiarów', 'wybor', { options: ['pozytywny', 'negatywny'] }),
        ],
      },
    ],
    declarations: ODBIOR_DECL.slice(0, 1),
  },
  {
    // QA qa10311123 3.16: rekuperacja (WM) — przepływy i regulacja, podpowiadane z listy czynności montażu WM
    code: 'montaz-WM',
    version: 1,
    kind: 'montaz',
    tradeCode: 'WM',
    title: 'Protokół odbioru montażu rekuperacji',
    devices: true,
    sections: [
      {
        title: 'Instalacja',
        fields: [
          f('dlugosc_kanalow', 'Długość kanałów wentylacyjnych', 'liczba', { unit: 'm', min: 0, max: 1000, required: false }),
          f('klasa_filtrow', 'Klasa filtrów', 'wybor', { options: ['G4', 'M5', 'F7', 'ePM1', 'inna'] }),
          f('szczelnosc', 'Szczelność kanałów i połączeń sprawdzona', 'tak_nie', { fromChecklist: 'Kontrola szczelności kanałów' }),
          f('skropliny', 'Odprowadzenie skroplin podłączone', 'tak_nie', { required: false }),
        ],
      },
      {
        title: 'Regulacja',
        fields: [
          f('nawiew', 'Przepływ nawiewu (suma)', 'liczba', { unit: 'm³/h', min: 0, max: 5000, fromChecklist: 'Pomiar przepływu nawiewu' }),
          f('wywiew', 'Przepływ wywiewu (suma)', 'liczba', { unit: 'm³/h', min: 0, max: 5000, fromChecklist: 'Pomiar przepływu wywiewu' }),
          f('regulacja', 'Regulacja anemostatów i zrównoważenie instalacji', 'tak_nie', { fromChecklist: 'Regulacja anemostatów' }),
          f('sterowanie', 'Ustawienia sterownika (biegi, harmonogram)', 'tekst', { required: false }),
          f('instruktaz', 'Instruktaż użytkownika (obsługa, wymiana filtrów)', 'tak_nie', { fromChecklist: 'Instruktaż klienta' }),
        ],
      },
    ],
    declarations: ODBIOR_DECL,
  },
  {
    code: 'montaz-ogolny',
    version: 1,
    kind: 'montaz',
    tradeCode: '*',
    title: 'Protokół odbioru prac',
    devices: true,
    sections: [{ title: 'Prace', fields: [f('opis_prac', 'Opis wykonanych prac', 'tekst'), f('test', 'Test działania wykonany', 'tak_nie')] }],
    declarations: ODBIOR_DECL,
  },
  {
    code: 'uruchomienie-PC',
    version: 1,
    kind: 'uruchomienie',
    tradeCode: 'PC',
    title: 'Protokół uruchomienia pompy ciepła',
    devices: true,
    sections: [
      {
        title: 'Parametry uruchomienia',
        fields: [
          f('cisnienie', 'Ciśnienie w instalacji', 'liczba', { unit: 'bar', min: 0, max: 6 }),
          f('temp_zasilania', 'Temperatura zasilania', 'liczba', { unit: '°C', min: 0, max: 80 }),
          f('krzywa', 'Ustawienia krzywej grzewczej', 'tekst', { required: false }),
          f('cwu', 'Ciepła woda użytkowa działa', 'tak_nie'),
          f('instruktaz', 'Instruktaż użytkownika', 'tak_nie'),
        ],
      },
    ],
    declarations: ['Urządzenia uruchomiono i przekazano do użytkowania. Data uruchomienia rozpoczyna okres gwarancji.', ...ODBIOR_DECL.slice(1)],
  },
  {
    code: 'uruchomienie-ogolny',
    version: 1,
    kind: 'uruchomienie',
    tradeCode: '*',
    title: 'Protokół uruchomienia',
    devices: true,
    sections: [{ title: 'Uruchomienie', fields: [f('parametry', 'Parametry pracy', 'tekst'), f('instruktaz', 'Instruktaż użytkownika', 'tak_nie')] }],
    declarations: ['Urządzenia uruchomiono i przekazano do użytkowania. Data uruchomienia rozpoczyna okres gwarancji.'],
  },
  {
    code: 'przeglad-KL',
    version: 1,
    kind: 'przeglad',
    tradeCode: 'KL',
    title: 'Protokół przeglądu klimatyzacji',
    devices: true,
    sections: [
      {
        title: 'Czynności przeglądu',
        fields: [
          f('filtry', 'Czyszczenie filtrów', 'tak_nie'),
          f('wymiennik', 'Czyszczenie wymiennika jednostki wewnętrznej', 'tak_nie'),
          f('dezynfekcja', 'Dezynfekcja / odgrzybianie', 'tak_nie'),
          f('szczelnosc', 'Kontrola szczelności', 'tak_nie'),
          f('temp_nawiewu', 'Temperatura nawiewu', 'liczba', { unit: '°C', min: -10, max: 60 }),
        ],
      },
    ],
    declarations: ['Przegląd wykonano zgodnie z wymaganiami gwarancyjnymi producenta.'],
  },
  {
    code: 'przeglad-PC',
    version: 1,
    kind: 'przeglad',
    tradeCode: 'PC',
    title: 'Protokół przeglądu pompy ciepła',
    devices: true,
    sections: [
      {
        title: 'Czynności przeglądu',
        fields: [
          f('cisnienie', 'Ciśnienie w instalacji', 'liczba', { unit: 'bar', min: 0, max: 6 }),
          f('filtr', 'Czyszczenie filtra siatkowego', 'tak_nie'),
          f('parownik', 'Kontrola i czyszczenie parownika', 'tak_nie'),
          f('bledy', 'Błędy w historii sterownika', 'tekst', { required: false }),
        ],
      },
    ],
    declarations: ['Przegląd wykonano zgodnie z wymaganiami gwarancyjnymi producenta.'],
  },
  {
    code: 'przeglad-ogolny',
    version: 1,
    kind: 'przeglad',
    tradeCode: '*',
    title: 'Protokół przeglądu',
    devices: true,
    sections: [{ title: 'Przegląd', fields: [f('czynnosci', 'Wykonane czynności', 'tekst'), f('sprawne', 'Urządzenia sprawne', 'tak_nie')] }],
    declarations: [],
  },
  {
    code: 'serwis-ogolny',
    version: 1,
    kind: 'serwis',
    tradeCode: '*',
    title: 'Protokół serwisowy',
    devices: true,
    sections: [
      {
        title: 'Naprawa',
        fields: [
          f('usterka', 'Stwierdzona usterka', 'tekst'),
          f('czynnosci', 'Wykonane czynności', 'tekst'),
          f('czesci', 'Wymienione części', 'tekst', { required: false }),
          f('sprawne', 'Urządzenie sprawne po naprawie', 'tak_nie'),
        ],
      },
    ],
    declarations: [],
  },
];

/**
 * QA qa10301331 P2: treść pola „Wymienione części” protokołu (klucz `czesci`, w protokole kilku branż `<branża>_czesci`).
 * Niepusta w podpisanym protokole = wymieniono część (reklamacja u producenta, lista części zgłoszenia).
 */
export function protocolReplacedParts(values: Record<string, ProtocolValue>): string {
  return Object.entries(values)
    .filter(([k, v]) => (k === 'czesci' || k.endsWith('_czesci')) && typeof v === 'string' && v.trim() !== '')
    .map(([, v]) => (v as string).trim())
    .join('; ');
}

/**
 * QA qa10302046 P10: wartości pól protokołu podpowiadane z listy czynności (pola z `fromChecklist`): liczba z wyniku
 * pomiaru (przecinek dziesiętny, jednostka pomijana), tekst — wynik, tak/nie — czynność wykonana. Protokół kilku branż
 * (klucz `<branża>_pole`) bierze tylko czynności swojej branży (i ogólne). Wypełnione pola nie są nadpisywane.
 */
export function protocolPrefill(
  t: Pick<ProtocolTemplateDef, 'sections' | 'tradeCode'>,
  checklist: { label: string; done: boolean; value: string; tradeCode?: string | null }[],
  values: Record<string, ProtocolValue> = {},
): Record<string, ProtocolValue> {
  const out = { ...values };
  const norm = (x: string) => x.trim().toLowerCase();
  for (const fd of t.sections.flatMap((s) => s.fields)) {
    if (!fd.fromChecklist) continue;
    const cur = out[fd.key];
    if (cur !== undefined && cur !== null && cur !== '') continue;
    const trade = t.tradeCode === 'MIX' ? (fd.key.split('_')[0] ?? null) : t.tradeCode === '*' ? null : t.tradeCode;
    const item = checklist.find((c) => norm(c.label) === norm(fd.fromChecklist!) && (!trade || !c.tradeCode || c.tradeCode === trade));
    if (!item) continue;
    if (fd.type === 'tak_nie') {
      if (item.done) out[fd.key] = true;
    } else if (fd.type === 'liczba') {
      const m = /-?\d+(?:[.,]\d+)?/.exec(item.value);
      const n = m ? Number(m[0].replace(',', '.')) : NaN;
      if (Number.isFinite(n)) out[fd.key] = n;
    } else if (fd.type === 'tekst' && item.value.trim()) out[fd.key] = item.value.trim();
  }
  return out;
}

/** Szablon dla rodzaju i branży — szczegółowy, a gdy go brak — ogólny („*”). */
export function findTemplate<T extends Pick<ProtocolTemplateDef, 'kind' | 'tradeCode'>>(templates: T[], kind: ProtocolKind, tradeCode: string | null): T | null {
  return templates.find((t) => t.kind === kind && t.tradeCode === tradeCode) ?? templates.find((t) => t.kind === kind && t.tradeCode === '*') ?? null;
}

export type ProtocolValue = string | number | boolean | null;

/** Błędy wartości pól (po polsku) — te same reguły na telefonie i na serwerze. */
export function validateProtocolValues(t: ProtocolTemplateDef, values: Record<string, ProtocolValue>): string[] {
  const errors: string[] = [];
  for (const fd of t.sections.flatMap((s) => s.fields)) {
    const v = values[fd.key];
    const empty = v === null || v === undefined || v === '';
    if (empty) {
      if (fd.required) errors.push(`Uzupełnij: ${fd.label}`);
      continue;
    }
    if (fd.type === 'liczba') {
      if (typeof v !== 'number' || !Number.isFinite(v)) errors.push(`${fd.label}: podaj liczbę`);
      else if ((fd.min !== undefined && v < fd.min) || (fd.max !== undefined && v > fd.max)) errors.push(`${fd.label}: wartość spoza zakresu ${fd.min ?? '…'}–${fd.max ?? '…'} ${fd.unit ?? ''}`.trim());
    } else if (fd.type === 'tak_nie') {
      if (typeof v !== 'boolean') errors.push(`${fd.label}: wybierz tak lub nie`);
    } else if (fd.type === 'wybor') {
      if (typeof v !== 'string' || !(fd.options ?? []).includes(v)) errors.push(`${fd.label}: wybierz z listy`);
    } else if (typeof v !== 'string' || v.length > 2000) errors.push(`${fd.label}: nieprawidłowy tekst`);
  }
  return errors;
}

/** Treść protokołu podpisywana przez klienta — wszystko, co trafia do PDF (docs/05 rozdz. 10). */
export interface ProtocolContent {
  format: 1;
  id: string;
  /** identyfikator nadany na telefonie, np. PO-7F3K92 (Z-05) */
  code: string;
  kind: ProtocolKind;
  title: string;
  template: { code: string; version: number };
  task: { id: string; number: string };
  company: { name: string; nip: string; address: string };
  client: { name: string };
  site: { address: string };
  scope: { tradeCode: string; tradeName: string; description: string } | null;
  /** scopeId — zakres (branża), do którego instalacji trafi urządzenie (protokół na umowę z kilkoma branżami) */
  devices: { name: string; serial: string; deviceId: string | null; scopeId?: string | null }[];
  values: Record<string, ProtocolValue>;
  checklist: { label: string; done: boolean; value: string }[];
  remarks: string;
  clientRemarks: string;
  photos: { id: string; sha256: string }[];
  /** adres e-mail klienta do wysyłki PDF (potwierdzony przy podpisie) */
  email: string | null;
  /** QA qa10302243 P6: klient otrzymał protokół na miejscu (bez e-maila) — pole tylko, gdy zaznaczone (zgodność starszych treści) */
  handedOver?: true;
  /**
   * Klienta nie było przy odbiorze — protokół przygotowany do podpisu zdalnego (pole tylko wtedy; zgodność starszych treści).
   * signedAt oznacza wtedy chwilę przygotowania, a signerName — osobę, która ma podpisać.
   */
  signMode?: ProtocolRemoteMethod;
  signerName: string;
  installer: { id: string; name: string };
  signedAt: string;
  /** docs/26: pozycje do zapłaty za serwis / przegląd (pole tylko, gdy są — skrót starszych treści bez zmian) */
  charges?: ProtocolCharges;
}

export interface SignatureData {
  /** kreski: punkty [x, y, ms od początku] w układzie 0–1000 × 0–400 */
  strokes: [number, number, number][][];
}

export const contentHash = (c: ProtocolContent) => sha256Hex(canonicalJson(c));
/** podpis powiązany ze skrótem treści */
export const signatureHash = (hash: string, s: SignatureData) => sha256Hex(canonicalJson({ contentHash: hash, strokes: s.strokes }));

const ALPHABET = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
/** Identyfikator protokołu z identyfikatora UUID (bez serii numeracji — Z-05). */
export function protocolCode(uuid: string): string {
  const hex = sha256Hex(uuid);
  let out = '';
  for (let i = 0; i < 6; i++) out += ALPHABET[parseInt(hex.slice(i * 2, i * 2 + 2), 16) % ALPHABET.length];
  return `PO-${out}`;
}

const fmt = (fd: ProtocolFieldDef | undefined, v: ProtocolValue) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'tak' : 'nie';
  if (typeof v === 'number') return `${String(v).replace('.', ',')}${fd?.unit ? ` ${fd.unit}` : ''}`;
  return v;
};

export interface ProtocolSection {
  title: string;
  rows: [string, string][];
}

/** Układ treści — wspólny dla podsumowania na telefonie i PDF (ten sam tekst, ta sama kolejność). */
export function protocolSections(c: ProtocolContent, t: ProtocolTemplateDef): ProtocolSection[] {
  const out: ProtocolSection[] = [
    {
      title: 'Dane',
      rows: [
        ['Protokół', `${c.code} (${c.template.code} v${c.template.version})`],
        ['Wykonawca', c.company.nip ? `${c.company.name}, NIP ${c.company.nip}` : c.company.name],
        ['Klient', c.client.name],
        ['Adres', c.site.address],
        ['Zadanie', c.task.number],
        ...(c.scope ? ([['Zakres', `${c.scope.tradeName}${c.scope.description ? ` — ${c.scope.description}` : ''}`]] as [string, string][]) : []),
      ],
    },
  ];
  if (t.devices) out.push({ title: 'Urządzenia', rows: c.devices.length ? c.devices.map((d) => [d.name, d.serial ? `SN ${d.serial}` : 'bez numeru seryjnego']) : [['—', 'brak urządzeń']] });
  for (const s of t.sections) out.push({ title: s.title, rows: s.fields.map((fd) => [fd.label, fmt(fd, c.values[fd.key] ?? null)]) });
  if (c.checklist.length) out.push({ title: 'Czynności', rows: c.checklist.map((x) => [x.label, `${x.done ? 'wykonano' : 'nie wykonano'}${x.value ? ` — ${x.value}` : ''}`]) });
  if (c.charges?.items.length) {
    out.push({
      title: 'Rozliczenie',
      rows: [
        ...c.charges.items.map((l) => [`${l.name} — ${chargeLineText(l)}`, formatMoney(chargeLineGr(l))] as [string, string]),
        ...(c.charges.reason ? ([['Powód (zgłoszenie gwarancyjne)', c.charges.reason]] as [string, string][]) : []),
        ['Razem do zapłaty (brutto)', formatMoney(c.charges.totalGr)],
      ],
    });
  }
  out.push({
    title: 'Uwagi',
    rows: [
      ['Uwagi wykonawcy', c.remarks || '—'],
      ['Uwagi i zastrzeżenia klienta', c.clientRemarks || '—'],
      ['Zdjęcia', c.photos.length ? `${c.photos.length} (skróty SHA-256 w treści protokołu)` : 'brak'],
    ],
  });
  const when = new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', dateStyle: 'long', timeStyle: 'short' }).format(new Date(c.signedAt));
  out.push({
    title: 'Podpis',
    rows: c.signMode
      ? [
          ['Podpisuje (klient)', c.signerName],
          ['Wykonał', c.installer.name],
          ['Przygotowano', when],
          ['Podpis klienta', c.signMode === 'epodpis' ? `elektronicznie — link na adres ${c.email ?? '—'}` : 'na wydruku (skan do systemu)'],
        ]
      : [
          ['Podpisał (klient)', c.signerName],
          ['Wykonał', c.installer.name],
          ['Data podpisu', when],
          ['Wysyłka PDF na adres', c.email ?? 'bez wysyłki e-mailem'],
        ],
  });
  return out;
}

/** Stan PDF i wysyłki widoczny na telefonie i w biurze (docs/05 rozdz. 11). */
export const PROTOCOL_DELIVERY_STATES = {
  czeka_na_synchronizacje: 'Czeka na synchronizację',
  czeka_na_podpis: 'Czeka na podpis klienta',
  oczekuje_na_zdjecia: 'Oczekuje na zdjęcia',
  oczekuje_na_pdf: 'Oczekuje na PDF',
  blad_pdf: 'Błąd generowania PDF',
  oczekuje_na_wysylke: 'Oczekuje na wysyłkę',
  wyslano: 'Wysłano (przyjęte przez serwer poczty)',
  blad_wysylki: 'Błąd wysyłki — ponawiam',
  blad_trwaly: 'Błąd wysyłki — wymaga działania',
  do_sprawdzenia: 'Wysyłka do sprawdzenia',
  bez_wysylki: 'PDF gotowy (bez wysyłki e-mailem)',
  odrzucony: 'Odrzucony — niezgodny skrót treści',
} as const;
export type ProtocolDeliveryState = keyof typeof PROTOCOL_DELIVERY_STATES;

export const PROTOCOL_STATUSES = { do_podpisu: 'Czeka na podpis klienta', podpisany: 'Podpisany', uniewazniony: 'Unieważniony' } as const;
export type ProtocolStatus = keyof typeof PROTOCOL_STATUSES;

/** Droga podpisu protokołu przez klienta. */
export const PROTOCOL_SIGN_METHODS = {
  na_miejscu: 'Na miejscu (telefon montera)',
  epodpis: 'Podpis elektroniczny (link i kod)',
  papier: 'Podpis papierowy (skan)',
} as const;
export type ProtocolSignMethod = keyof typeof PROTOCOL_SIGN_METHODS;
/** Podpis zdalny — klienta nie było przy odbiorze. */
export type ProtocolRemoteMethod = Exclude<ProtocolSignMethod, 'na_miejscu'>;

/** Protokół w pakiecie zadania (telefon) i w biurze. */
export interface FieldProtocol {
  id: string;
  code: string;
  slotKey: string;
  kind: ProtocolKind;
  status: ProtocolStatus;
  /** chwila podpisu klienta; protokół czekający na podpis zdalny — chwila przygotowania (treść) */
  signedAt: string;
  signerName: string;
  /** droga podpisu (starsze serwery nie wysyłają — na miejscu) */
  signMethod?: ProtocolSignMethod;
  contentHash: string;
  delivery: ProtocolDeliveryState;
  email: string | null;
  voidReason: string;
  /** uwagi klienta przy odbiorze (docs/10 §3.6): brak · uwagi · poprawka (zlecona) · rozpatrzone */
  remarksStatus?: RemarksStatus;
  clientRemarks?: string;
  remarksResolution?: string;
  /** podpisany przy niepełnej checkliście (P5) — braki w chwili przyjęcia protokołu przez serwer */
  incompleteChecklist?: string[];
  /** QA qa10302243 P6: doręczony klientowi poza e-mailem (na miejscu przy podpisie albo oznaczony w biurze) */
  handedOver?: boolean;
}

export const REMARKS_STATUSES = { brak: 'Bez uwag', uwagi: 'Uwagi klienta — do rozpatrzenia', poprawka: 'Poprawka zlecona', rozpatrzone: 'Uwagi rozpatrzone' } as const;
export type RemarksStatus = keyof typeof REMARKS_STATUSES;

/** Czy wpis „Uwagi i zastrzeżenia klienta” zawiera uwagi (a nie „brak zastrzeżeń”, „bez uwag”, „-”). */
export function hasClientRemarks(text: string | null | undefined): boolean {
  const t = (text ?? '').trim().toLowerCase().replace(/[.!]+$/, '');
  if (!t) return false;
  return !/^(brak( uwag| zastrzeżeń| zastrzezen)?|bez (uwag|zastrzeżeń|zastrzezen)|nie ma|nie|ok|-+|—)$/.test(t);
}

/** Miejsce na protokół w zadaniu: per zakres (firma zakresu) albo jedno dla zgłoszenia serwisowego. */
export interface ProtocolSlot {
  key: string;
  kind: ProtocolKind;
  /** pierwszy zakres; scopeIds — wszystkie zakresy umowy objęte protokołem */
  scopeId: string | null;
  scopeIds: string[];
  tradeCode: string;
  tradeName: string;
  scopeDescription: string;
  company: { name: string; nip: string; address: string };
  template: ProtocolTemplateDef;
  /** urządzenia do wpisania numerów seryjnych (z umowy lub z karty instalacji) */
  /** scopeId — zakres (branża), do którego instalacji trafi urządzenie (protokół na umowę z kilkoma branżami) */
  devices: { name: string; serial: string; deviceId: string | null; scopeId?: string | null; warrantyUntil?: string | null }[];
  /**
   * protokół serwisowy: pozostałe czynne urządzenia instalacji (spoza zgłoszenia) — serwisant może je dopisać (D15);
   * praca z aneksu: urządzenia odebrane wcześniej (P10) — dopisywane tylko, gdy ich dotyczy praca, i usuwalne z listy
   */
  otherDevices?: { name: string; serial: string; deviceId: string; warrantyUntil: string | null; scopeId?: string | null }[];
  /** zakresy (branże) protokołu — przy kilku branżach monter wybiera branżę dopisywanego urządzenia (P20) */
  scopes?: { scopeId: string; tradeCode: string; tradeName: string }[];
  /** praca z podpisanego aneksu (umowa miała już własną realizację) — urządzenia z pozycji aneksu (P10) */
  annexNumbers?: string[];
  required: boolean;
}

/**
 * Brakujące protokoły (wymagane, bez podpisanego i nieunieważnionego). Protokół wysłany do podpisu zdalnego
 * (e-podpis / papier) zamyka pracę ekipy — odbiór zakresu czeka osobno na podpis klienta.
 */
export function missingProtocols(slots: ProtocolSlot[], protocols: Pick<FieldProtocol, 'slotKey' | 'status'>[]): ProtocolSlot[] {
  return slots.filter((s) => s.required && !protocols.some((p) => p.slotKey === s.key && (p.status === 'podpisany' || p.status === 'do_podpisu')));
}

/** Stan podpisu zdalnego protokołu (biuro: okno zadania, karta sprawy). */
export interface ProtocolSigningDto {
  method: ProtocolSignMethod;
  status: ProtocolStatus;
  /** chwila podpisu klienta (null — czeka) */
  signedAt: string | null;
  signerName: string;
  /** kto zarejestrował podpis papierowy */
  recordedByName: string | null;
  /** uwagi klienta zgłoszone przy podpisie zdalnym */
  signerRemarks: string;
  /** wysyłka do podpisu elektronicznego (ostatnia) */
  esign: {
    requestId: string;
    status: import('./esign').EsignStatus;
    email: string;
    phone: string;
    channel: import('./esign').OtpChannel;
    sentAt: string;
    expiresAt: string;
    viewCount: number;
    lastLinkAt: string | null;
    events: { at: string; type: string; label: string }[];
  } | null;
  /** skan podpisanego wydruku jest w systemie (albo czeka na zdjęcia z telefonu) */
  scan: 'brak' | 'czeka_na_zdjecia' | 'jest';
  can: { send: boolean; resend: boolean; cancel: boolean; paper: boolean; print: boolean };
}

/** Uwagi klienta protokołu: zgłoszone przy podpisie zdalnym albo wpisane na miejscu (treść). */
export const protocolClientRemarks = (p: { signerRemarks?: string | null; content?: { clientRemarks?: string } | null }) =>
  (p.signerRemarks?.trim() || p.content?.clientRemarks?.trim() || '').trim();

/**
 * Szablon łączony — jedna umowa na kilka branż ma jeden protokół odbioru: sekcje i pola wszystkich branż
 * (klucze pól z prefiksem branży), oświadczenia bez powtórzeń. Kod zawiera wersje składowych — zmiana wzoru
 * branży daje nową wersję szablonu łączonego.
 */
export function combineTemplates(kind: ProtocolKind, parts: { tradeCode: string; tradeName: string; template: ProtocolTemplateDef }[]): ProtocolTemplateDef {
  if (parts.length === 1) return parts[0]!.template;
  return {
    code: `${kind}-${parts.map((p) => `${p.tradeCode}${p.template.version}`).join('+')}`.slice(0, 40),
    version: 1,
    kind,
    tradeCode: 'MIX',
    title: `${PROTOCOL_KINDS[kind]} — ${parts.map((p) => p.tradeName).join(', ')}`,
    devices: parts.some((p) => p.template.devices),
    sections: parts.flatMap((p) =>
      p.template.sections.map((sec) => ({ title: `${p.tradeName} — ${sec.title}`, fields: sec.fields.map((f) => ({ ...f, key: `${p.tradeCode}_${f.key}` })) })),
    ),
    declarations: [...new Set(parts.flatMap((p) => p.template.declarations))],
  };
}

/** Rozpatrzenie uwag klienta: poprawka dla ekipy albo uznanie uwag za rozpatrzone (z komentarzem). */
export const remarksResolveSchema = z.object({ comment: z.string().trim().min(3, 'Napisz, jak rozpatrzono uwagi').max(1000) });
export const reworkCreateSchema = z.object({ description: z.string().trim().min(3, 'Opisz, co ekipa ma poprawić').max(4000), title: z.string().trim().max(200).default('') });
